import { DOCS_FEEDBACK_RATING_TYPES } from "core/consts";
import {
  DocsFieldFeedback,
  DocsFormDataMapped,
  DocsFormName,
} from "core/types";
import { Button, Form, Textarea } from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { ThumbsDownIcon, ThumbsUpIcon, XIcon } from "lucide-react";
import { FormEvent, useState } from "react";
import { useTranslations } from "translations";
import type { SourceInformation } from "./useDocsForm";

type DocsFeedbackPanelProps<T extends DocsFormName> = {
  activeFeedbackPanel: Pick<DocsFieldFeedback, "label" | "fieldName">;
  sourceInformation: SourceInformation<T>;
  onClose: () => void;
  onSave: (description: string) => void;
};

export function DocsFeedbackPanel<T extends DocsFormName>({
  activeFeedbackPanel,
  onClose,
  onSave,
  sourceInformation,
}: DocsFeedbackPanelProps<T>) {
  const translations = useTranslations();
  const fieldKey = activeFeedbackPanel.fieldName as keyof DocsFormDataMapped<T>;
  const [description, setDescription] = useState(
    () => sourceInformation.getSourceFields(fieldKey)?.rating_description ?? "",
  );
  const toast = useToast();

  const rating = sourceInformation.getSourceFields(fieldKey)?.rating;
  const isPositive = rating === DOCS_FEEDBACK_RATING_TYPES.POSITIVE;
  const textLabel = isPositive
    ? `${activeFeedbackPanel.label} - ${translations.extractDocs.userFeedback.fieldLabelPositive} `
    : `${activeFeedbackPanel.label} - ${translations.extractDocs.userFeedback.fieldLabelNegative}`;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave(description);
    toast({
      color: "success",
      message: translations.extractDocs.userFeedback.toastMessageSuccess,
    });
  };

  return (
    <Form
      className="flex h-full w-[50%] min-w-0 flex-col gap-3 overflow-hidden border-l border-gray-400 p-6"
      onSubmit={handleSubmit}
    >
      <div className="flex w-full justify-between pb-4">
        <div className="flex gap-2">
          {isPositive ? (
            <ThumbsUpIcon className="text-success" size={20} />
          ) : (
            <ThumbsDownIcon className="text-danger" size={20} />
          )}
          <h3 className="heading-md font-semibold">
            {translations.extractDocs.userFeedback.titleProvideFeedback}
          </h3>
        </div>
        <Button
          aria-label={translations.actions.close}
          color="black"
          id="close-feedback"
          isIconOnly
          size="sm"
          type="button"
          variant="light"
          onPress={onClose}
        >
          <XIcon size={14} />
        </Button>
      </div>
      <Textarea
        key={rating}
        label={textLabel}
        placeholder={
          isPositive
            ? translations.extractDocs.userFeedback.fieldPlaceholderPositive
            : translations.extractDocs.userFeedback.fieldPlaceholderNegative
        }
        size="sm"
        value={description}
        onValueChange={setDescription}
      />
      <div className="flex w-full justify-end gap-2 pt-2">
        <Button
          id="cancel-feedback"
          type="button"
          variant="light"
          onPress={onClose}
        >
          {translations.actions.cancel}
        </Button>
        <Button color="primary" id="save-feedback" type="submit">
          {translations.extractDocs.userFeedback.submitWrittenFeedback}
        </Button>
      </div>
    </Form>
  );
}
